// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import knowledgeService from '../../services/knowledgeService';

/**
 * Multi-select Knowledge articles by search + optional paste source_key.
 * `value` is knowledge_items.id[]; `onChange` receives the next id list.
 */
function KnowledgeItemIdsField({
  value = [],
  onChange,
  disabled = false,
  helperText = '',
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [pasteKey, setPasteKey] = useState('');
  const [selected, setSelected] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function enrich(ids) {
      if (!ids?.length) {
        if (!cancelled) setSelected([]);
        return;
      }
      const next = [];
      for (const id of ids) {
        try {
          const r = await knowledgeService.resolveKnowledgeItem({ knowledge_item_id: id });
          next.push({ id: r.id, title: r.title, source_key: r.source_key });
        } catch {
          next.push({ id, title: id, source_key: '' });
        }
        if (cancelled) return;
      }
      if (!cancelled) setSelected(next);
    }
    enrich(value);
    return () => {
      cancelled = true;
    };
  }, [value]);

  const runSearch = useCallback((q) => {
    const t = (q || '').trim();
    if (t.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    knowledgeService
      .listKnowledgeItems({ q: t, limit: 20 })
      .then((res) => {
        setOptions(
          (res.items || []).map((r) => ({
            id: r.id,
            title: r.title,
            source_key: r.source_key || '',
          })),
        );
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, []);

  const handleInputChange = (_, v) => {
    setInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 300);
  };

  const emit = (rows) => {
    onChange(rows.map((r) => r.id));
  };

  const addResolved = async () => {
    const sk = pasteKey.trim();
    if (!sk) return;
    try {
      const r = await knowledgeService.resolveKnowledgeItem({ source_key: sk });
      if (selected.some((s) => s.id === r.id)) {
        setPasteKey('');
        return;
      }
      const next = [...selected, { id: r.id, title: r.title, source_key: r.source_key }];
      setSelected(next);
      emit(next);
      setPasteKey('');
    } catch {
      /* handled by parent or silent */
    }
  };

  return (
    <Stack spacing={1}>
      {helperText ? (
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
      <Autocomplete
        multiple
        options={options}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(o) =>
          o?.title ? `${o.title} — ${o.source_key || o.id}` : ''
        }
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={selected}
        onChange={(_, v) => {
          setSelected(v);
          emit(v);
        }}
        inputValue={input}
        onInputChange={handleInputChange}
        filterOptions={(x) => x}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.source_key || option.title}
              {...getTagProps({ index })}
              key={option.id}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search knowledge articles"
            placeholder="Type at least 2 characters…"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </Box>
              ),
            }}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          fullWidth
          label="Paste source key"
          value={pasteKey}
          onChange={(e) => setPasteKey(e.target.value)}
          placeholder="e.g. faq:my-article:en"
          disabled={disabled}
        />
        <Button variant="outlined" disabled={disabled || !pasteKey.trim()} onClick={addResolved}>
          Add
        </Button>
      </Stack>
    </Stack>
  );
}

export default KnowledgeItemIdsField;
